import React, { Component, Fragment } from 'react';
import { Observer, observer } from 'mobx-react';
import { observable } from 'mobx';

import { Panel, PanelType } from 'office-ui-fabric-react/lib/Panel';
import {
  DefaultButton,
  PrimaryButton,
} from 'office-ui-fabric-react/lib/Button';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';

import Loading from 'components/LoadingOverlay';
import { localizable } from 'components/Localization';
import { FooterContainer } from 'components/PanelFooter';

import { AuctionFinalsStore } from './stores';
import {
  AuctionFinalsMatrix,
  AuctionFinalsList,
  AuctionFinalsTotals,
} from './components';

@localizable
@observer
export default class AuctionFinalsPanel extends Component {
  @observable phase = 'select'; // select, review, awarding, results

  @observable loading = false;
  @observable loaded = false;
  @observable error = false;

  store = null; // AuctionFinalsStore
  abortController; // = new AbortController()

  constructor(props) {
    super(props);
    this.store = new AuctionFinalsStore(props.sale); // sale = SaleStore
  }

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.abortController =
      typeof window.AbortController !== 'undefined'
        ? new window.AbortController()
        : null;

    this.loaded = false;
    this.loading = true;
    this.error = false;
    try {
      await this.store.load(
        this.abortController ? this.abortController.signal : null
      );
      this.loading = false;
      this.loaded = true;
      this.abortController = null;
    } catch (error) {
      console.error(error);
      this.loading = false;
      this.error = true;
      this.abortController = null;
    }
  };

  abort = () => {
    if (this.abortController) this.abortController.abort();
    this.abortController = null;
  };

  handleCancel = () => {
    this.abort();
    this.phase = 'results';
  };

  handleClose = () => {
    this.abort();
    const { onClose } = this.props;
    if (onClose) {
      onClose();
    }
  };

  handleSetPhase = (phase) => () => {
    this.phase = phase;
    this.error = false;
  };

  handleBackFromResults = () => {
    // remove awarded offers from selection
    this.store.removeAwarded();
    this.phase = 'review';
  };

  handleAward = async () => {
    this.abortController =
      typeof window.AbortController !== 'undefined'
        ? new window.AbortController()
        : null;

    this.phase = 'awarding';
    this.error = false;
    try {
      await this.store.award(
        this.abortController ? this.abortController.signal : null
      );
      this.phase = 'results';
    } catch (error) {
      console.error(error);
      this.error = true;
    }
  };

  render() {
    const { sale, t } = this.props;
    const { selected, processed, results } = this.store;

    return (
      <Panel
        type={PanelType.smallFluid}
        isOpen={true}
        onDismiss={this.handleClose}
        isFooterAtBottom={true}
        headerText={
          this.phase === 'select'
            ? t('Select winners')
            : this.phase === 'review'
            ? t('Review winners')
            : t('Report')
        }
        onRenderFooterContent={() => (
          <Observer
            render={() => (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                {(this.phase === 'select' || this.phase === 'review') &&
                  this.loaded && (
                    <strong>
                      <AuctionFinalsTotals
                        store={this.store}
                        locale={sale.saleEdit.locale}
                        currency={sale.saleEdit.currencyCode}
                      />
                    </strong>
                  )}
                {(this.phase === 'awarding' || this.phase === 'results') && (
                  <strong>
                    {t('Succeeded')}: {results.succeeded.length}, {t('Failed')}:
                    {results.failed.length}
                  </strong>
                )}
                <FooterContainer>
                  {this.phase === 'select' && (
                    <Fragment>
                      <DefaultButton onClick={this.handleClose}>
                        {t('Close')}
                      </DefaultButton>
                      {this.loaded && (
                        <PrimaryButton
                          onClick={this.handleSetPhase('review')}
                          disabled={this.store.selectedCount === 0}
                        >
                          {t('Continue')}
                        </PrimaryButton>
                      )}
                    </Fragment>
                  )}
                  {this.phase === 'review' && (
                    <Fragment>
                      <DefaultButton onClick={this.handleSetPhase('select')}>
                        {t('Back')}
                      </DefaultButton>
                      <DefaultButton onClick={this.handleClose}>
                        {t('Close')}
                      </DefaultButton>
                      <PrimaryButton
                        onClick={this.handleAward}
                        disabled={this.store.selectedCount === 0}
                      >
                        <Fragment>{t('Confirm')}</Fragment>
                      </PrimaryButton>
                    </Fragment>
                  )}
                  {this.phase === 'awarding' && (
                    <Fragment>
                      <DefaultButton onClick={this.handleCancel}>
                        {t('Cancel')}
                      </DefaultButton>
                      <PrimaryButton disabled={true}>
                        <Fragment>
                          {t('Confirm')}
                          {` (${processed + 1} / ${selected})`}
                          <Spinner
                            size={SpinnerSize.xSmall}
                            style={{ paddingLeft: '10px' }}
                          />
                        </Fragment>
                      </PrimaryButton>
                    </Fragment>
                  )}

                  {this.phase === 'results' && (
                    <Fragment>
                      <DefaultButton onClick={this.handleBackFromResults}>
                        {t('Back')}
                      </DefaultButton>
                      <DefaultButton onClick={this.handleClose}>
                        {t('Close')}
                      </DefaultButton>
                    </Fragment>
                  )}
                </FooterContainer>
              </div>
            )}
          />
        )}
        closeButtonAriaLabel="Close"
      >
        {this.phase === 'select' && (
          <Fragment>
            {this.loaded && (
              <div style={{ width: 'calc(100% - 80)' }}>
                <Observer
                  render={() => (
                    <Fragment>
                      <AuctionFinalsMatrix
                        sale={sale}
                        store={this.store}
                        locale={sale.saleEdit.locale}
                        currency={sale.saleEdit.currencyCode}
                      />
                    </Fragment>
                  )}
                ></Observer>
              </div>
            )}
            {!this.loaded && (
              <Fragment>
                {this.loading && <Loading />}
                {this.error && (
                  <div
                    style={{
                      marginTop: '50vh',
                      textAlign: 'center',
                      color: 'Red',
                    }}
                  >
                    {t('Error loading data from server')}
                  </div>
                )}
              </Fragment>
            )}
          </Fragment>
        )}
        {this.phase !== 'select' && (
          <AuctionFinalsList
            sale={sale}
            store={this.store}
            locale={sale.saleEdit.locale}
            currency={sale.saleEdit.currencyCode}
            showResults={this.phase === 'awarding' || this.phase === 'results'}
          />
        )}
      </Panel>
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/components/AuctionFinals/AuctionFinalsPanel.jsx